import { css, keyframes } from "@emotion/react";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import {
  CAPTION,
  FIELD_BORDER,
  FIELD_DISABLED_BG,
  FIELD_HOVER_BORDER,
  FOCUS_RING,
  HOVER_SURFACE,
  HOVER_TRANSITION_MS,
  MENU_SHADOW,
  REDUCED_MOTION_SX,
} from "src/domains/shared/components/StateTransition";

export const SELECT_FIELD_HEIGHT_PX = 32;
/** 목록 최대 높이 — `mypage-project-history` 스펙의 300px 제한. */
export const SELECT_MENU_MAX_HEIGHT_PX = 300;
export const SELECT_MENU_OPEN_MS = 200;

export type SelectFieldState = "idle" | "open" | "off";

/** 칸 크기 — `small` 은 마이페이지 밀도(32px·13px), `medium` 은 넓은 입력 창(36px·14px). */
export type SelectSize = "small" | "medium";

const SELECT_SIZE = {
  small: { height: SELECT_FIELD_HEIGHT_PX, padding: "0 6px 0 10px", fontSize: 13, fontWeight: 500, optionMinHeight: 34, optionPadding: "7px 12px 7px 14px" },
  medium: { height: 36, padding: "0 8px 0 12px", fontSize: 14, fontWeight: 400, optionMinHeight: 36, optionPadding: "8px 12px 8px 14px" },
} as const;

/** 칸 안 글자 크기 — 검색형 입력·버튼형 라벨이 칸 크기를 따른다. */
export const selectTextSizeSx = (size: SelectSize) => ({
  fontSize: SELECT_SIZE[size].fontSize,
  fontWeight: SELECT_SIZE[size].fontWeight,
});

export const selectMenuOpenKeyframes = keyframes`
  from { opacity: 0; transform: scale(0.98, 0.9); }
  to { opacity: 1; transform: none; }
`;

export const selectMenuAboveCss = css`
  top: auto;
  bottom: calc(100% + 4px);
  transform-origin: bottom center;
`;

export const selectMenuCss = css`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 8;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  max-height: ${SELECT_MENU_MAX_HEIGHT_PX}px;
  overflow-y: auto;
  background: ${COLOR_CONSTANTS.common.white_states.main};
  border-radius: 8px;
  box-shadow: ${MENU_SHADOW};
  transform-origin: top center;
  animation: ${selectMenuOpenKeyframes} ${SELECT_MENU_OPEN_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const selectFieldSx = (state: SelectFieldState, size: SelectSize = "small") => ({
  height: SELECT_SIZE[size].height,
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: SELECT_SIZE[size].padding,
  borderRadius: "6px",
  border: `1px solid ${state === "open" ? COLOR_CONSTANTS.primary.main : FIELD_BORDER}`,
  boxShadow: state === "open" ? FOCUS_RING : "none",
  backgroundColor: state === "off" ? FIELD_DISABLED_BG : COLOR_CONSTANTS.common.white_states.main,
  color: state === "off" ? COLOR_CONSTANTS.text.disabled : COLOR_CONSTANTS.text.primary,
  cursor: state === "off" ? "default" : "pointer",
  transition: "border-color 150ms ease, box-shadow 150ms ease",
  "&:hover": state === "idle" ? { borderColor: FIELD_HOVER_BORDER } : undefined,
  "&:focus-visible": { outline: "none", borderColor: COLOR_CONSTANTS.primary.main, boxShadow: FOCUS_RING },
  ...REDUCED_MOTION_SX,
});

export const selectInputSx = {
  flex: 1,
  minWidth: 0,
  height: "100%",
  border: 0,
  outline: "none",
  padding: 0,
  background: "transparent",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 500,
  color: "inherit",
  textOverflow: "ellipsis",
  cursor: "inherit",
  "&::placeholder": { color: COLOR_CONSTANTS.text.secondary, opacity: 1 },
  "&:disabled::placeholder": { color: COLOR_CONSTANTS.text.disabled },
} as const;

export const selectLabelSx = (isPlaceholder: boolean, size: SelectSize = "small") => ({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...selectTextSizeSx(size),
  color: isPlaceholder ? COLOR_CONSTANTS.text.secondary : "inherit",
});

export const selectChevronSx = (open: boolean) => ({
  display: "flex",
  opacity: 0.6,
  transform: open ? "rotate(180deg)" : "none",
  transition: `transform ${HOVER_TRANSITION_MS}ms ease-out`,
  ...REDUCED_MOTION_SX,
});

export const selectOptionSx = (isSelected: boolean, isActive: boolean, size: SelectSize = "small") => {
  const background = (() => {
    if (isSelected) return COLOR_CONSTANTS.primary._states.selected;
    if (isActive) return HOVER_SURFACE;
    return "transparent";
  })();
  return {
    minHeight: SELECT_SIZE[size].optionMinHeight,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: SELECT_SIZE[size].optionPadding,
    fontSize: SELECT_SIZE[size].fontSize,
    lineHeight: 1.45,
    fontWeight: isSelected ? 600 : 400,
    color: isSelected ? COLOR_CONSTANTS.primary.main : COLOR_CONSTANTS.text.primary,
    backgroundColor: background,
    cursor: "pointer",
    transition: "background-color 120ms ease",
    ...REDUCED_MOTION_SX,
  };
};

export const selectOptionLabelSx = {
  flex: 1,
  minWidth: 0,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-all",
} as const;

/** 항목 오른쪽 선택 표시(✓) 자리 — 선택되지 않은 항목도 같은 폭을 잡아 라벨 줄바꿈 위치가 같다. */
export const selectOptionCheckSx = {
  width: 16,
  height: 16,
  flexShrink: 0,
  color: COLOR_CONSTANTS.primary.main,
} as const;

export const selectEmptySx = {
  height: 34,
  display: "flex",
  alignItems: "center",
  padding: "0 14px",
  fontSize: 13,
  color: CAPTION,
} as const;
